/**
 * Mock trigger API stubs — used when VITE_API_MODE=mock.
 * Returns enough data for the OmegaTriggersPage to render.
 */

import type {
  CreateTriggerRequest,
  CreateTriggerResponse,
  CronValidateResponse,
  FireTriggerResponse,
  SetTriggerEnabledRequest,
  SetTriggerEnabledResponse,
  TriggerFireEvent,
  TriggerHealthStats,
  TriggerSummary,
} from '../types';

const MOCK_TRIGGERS: TriggerSummary[] = [
  {
    trigger_id: 'aaaaaaaa-0000-0000-0000-000000000001',
    specialist_id: 'market-research',
    trigger_type: { kind: 'cron', expression: '0 * * * *' },
    enabled: true,
    consecutive_failures: 0,
  },
  {
    trigger_id: 'bbbbbbbb-0000-0000-0000-000000000001',
    specialist_id: 'anomaly-detector',
    trigger_type: { kind: 'webhook' },
    enabled: true,
    consecutive_failures: 2,
  },
  {
    trigger_id: 'cccccccc-0000-0000-0000-000000000001',
    specialist_id: 'data-pipeline',
    trigger_type: { kind: 'log_event', entry_type_filter: 'entity' },
    enabled: false,
    consecutive_failures: 5,
  },
];

export async function listTriggers(): Promise<TriggerSummary[]> {
  return structuredClone(MOCK_TRIGGERS);
}

export async function getTriggerHealth(): Promise<TriggerHealthStats> {
  return {
    total_enabled: 2,
    total_disabled: 1,
    currently_failing: 2,
    auto_disabled_count: 1,
    overdue_cron_count: 0,
  };
}

export async function getTriggerRecentEvents(
  _id: string,
): Promise<TriggerFireEvent[]> {
  const now = Date.now();
  return [
    {
      sequence: 42,
      fired_at: new Date(now - 60_000).toISOString(),
      source: 'cron',
      specialist_id: 'market-research',
    },
    {
      sequence: 40,
      fired_at: new Date(now - 3_600_000).toISOString(),
      source: 'cron',
      specialist_id: 'market-research',
    },
  ];
}

export async function createTrigger(
  req: CreateTriggerRequest,
  _founderSig: string,
): Promise<CreateTriggerResponse> {
  return {
    trigger_id: crypto.randomUUID(),
    specialist_id: req.specialist_id,
    trigger_type: req.trigger_type,
    created_at: new Date().toISOString(),
  };
}

export async function setTriggerEnabled(
  id: string,
  _req: SetTriggerEnabledRequest,
  enabled: boolean,
  _founderSig: string,
): Promise<SetTriggerEnabledResponse> {
  return { trigger_id: id, enabled, updated_at: new Date().toISOString() };
}

export async function fireTrigger(id: string): Promise<FireTriggerResponse> {
  return {
    trigger_id: id,
    specialist_id: 'mock-specialist',
    fired_at: new Date().toISOString(),
    message: 'trigger queued for execution (Phase 3)',
  };
}

export async function validateCronExpression(
  expression: string,
): Promise<CronValidateResponse> {
  // Very basic client-side validation — just checks non-empty 5-field format.
  const parts = expression.trim().split(/\s+/);
  if (parts.length < 5) {
    return { valid: false, next_fire_times: [], error: 'Expected 5 cron fields' };
  }
  const now = Date.now();
  return {
    valid: true,
    next_fire_times: Array.from({ length: 5 }, (_, i) =>
      new Date(now + (i + 1) * 3_600_000).toISOString(),
    ),
  };
}
